/**
 * Default values for the site-content global. Shared boilerplate that surfaces
 * across pages: suburb list, footer disclaimer, mobile menu blurb, fallback
 * trust strip testimonial.
 */

export type FallbackTestimonial = {
  quote: string
  author: string
  location: string
}

export type ServiceArea = {
  name: string
  type: 'Place' | 'AdministrativeArea'
}

/**
 * A suburb in the service area list. Drives both the footer "Home turf" line
 * (name + order) and the home page service-area map (x/y/isHome). x and y are
 * percentages on the eastern Victoria satellite image: x% = (lon - 144.8) /
 * 3.2 * 100, y% = (|lat| - 37.2) / 1.3 * 100. Leave x/y blank to skip the map.
 */
export type Suburb = {
  name: string
  x?: number
  y?: number
  isHome?: boolean
}

export type ServiceAreaPhrasing = {
  homeBase: string
  region: string
  wider: string
}

/**
 * Before/after driveway-restoration clip used on home and services.
 * Single source of truth so the asset path and alt text only ever live in one place.
 */
export type DrivewayClip = {
  beforeSrc: string
  afterSrc: string
  beforeAlt: string
  afterAlt: string
}

/** Small labels in client components: sticky mobile bar, trust strip, before/after slider. */
export type SiteLabels = {
  stickyCtaLabel: string
  googleReviewsLabel: string
  trustStripAbnLabel: string
  trustStripInsuredLabel: string
  beforeLabel: string
  afterLabel: string
}

export type ReviewPageCopy = {
  metaTitle: string
  heading: string
  intro: string
  askHeading: string
  supportIntro: string
  firstNameLabel: string
  lastNameLabel: string
  phoneLabel: string
  emailLabel: string
  contactHint: string
  messageLabel: string
  messagePlaceholder: string
  submitLabel: string
  sendingLabel: string
  googleLinkLabel: string
}

export type TestimonialPageCopy = {
  metaTitle: string
  metaDescription: string
  eyebrow: string
  heading: string
  intro: string
}

export type OtherPages = {
  review: ReviewPageCopy
  testimonial: TestimonialPageCopy
}

export type SiteContent = {
  suburbs: Suburb[]
  footerDisclaimer: string
  footerLocationBlurb: string
  mobileMenuBlurb: string
  serviceAreaPhrasing: ServiceAreaPhrasing
  fallbackTestimonial: FallbackTestimonial
  serviceAreas: ServiceArea[]
  capabilities: string[]
  additionalOffer: {
    name: string
    description: string
  }
  drivewayClip: DrivewayClip
  siteLabels: SiteLabels
  otherPages: OtherPages
}

export const siteContentDefaults: SiteContent = {
  suburbs: [
    { name: 'Belgrave', x: 17.3, y: 54.5 },
    { name: 'Emerald', x: 20.0, y: 56.3 },
    { name: 'Cockatoo', x: 21.7, y: 56.8, isHome: true },
    { name: 'Gembrook', x: 23.6, y: 58.0 },
    { name: 'Maryknoll', x: 23.4, y: 63.5 },
    { name: 'Healesville', x: 22.4, y: 34.9 },
    { name: 'Yarra Junction', x: 25.5, y: 44.7 },
    { name: 'Launching Place', x: 24.4, y: 44.6 },
    { name: 'Warburton', x: 27.8, y: 42.5 },
    { name: 'Narre Warren', x: 15.7, y: 63.7 },
    { name: 'Berwick', x: 17.1, y: 64.2 },
    { name: 'Beaconsfield', x: 17.7, y: 65.1 },
    { name: 'Officer', x: 19.2, y: 66.1 },
    { name: 'Pakenham', x: 21.5, y: 66.9 },
    { name: 'Drouin', x: 33.1, y: 72.1 },
    { name: 'Warragul', x: 35.3, y: 73.9 },
    { name: 'Moe', x: 39.4, y: 75.2 },
    { name: 'Traralgon', x: 54.4, y: 76.7 },
    { name: 'Sale', x: 70.8, y: 70.1 },
    { name: 'Bairnsdale', x: 87.9, y: 48.2 },
    { name: 'Leongatha', x: 35.9, y: 89 },
  ],
  footerDisclaimer:
    'All prices inc. GST. Final pricing confirmed on-site. Pricing reviewed periodically to reflect cost changes.',
  footerLocationBlurb:
    'Based in {homeBase}, serving the {region} and {wider}. Salt of the **Earthworks|chartreuse**.',
  mobileMenuBlurb:
    'Based in {homeBase}, serving the {region} and {wider}. Salt of the **Earthworks|chartreuse**.',
  serviceAreaPhrasing: {
    homeBase: 'Cockatoo',
    region: 'Dandenong Ranges',
    wider: 'eastern Victoria',
  },
  fallbackTestimonial: {
    quote: 'Called Tuesday, done Thursday.',
    author: 'Bec H.',
    location: 'Emerald',
  },
  serviceAreas: [
    { name: 'Dandenong Ranges', type: 'Place' },
    { name: 'Gippsland', type: 'Place' },
    { name: 'West Gippsland', type: 'Place' },
    { name: 'Eastern Victoria', type: 'Place' },
    { name: 'Cockatoo, Victoria', type: 'Place' },
    { name: 'Emerald, Victoria', type: 'Place' },
    { name: 'Gembrook, Victoria', type: 'Place' },
    { name: 'Belgrave, Victoria', type: 'Place' },
    { name: 'Maryknoll, Victoria', type: 'Place' },
    { name: 'Healesville, Victoria', type: 'Place' },
    { name: 'Yarra Junction, Victoria', type: 'Place' },
    { name: 'Launching Place, Victoria', type: 'Place' },
    { name: 'Warburton, Victoria', type: 'Place' },
    { name: 'Narre Warren, Victoria', type: 'Place' },
    { name: 'Berwick, Victoria', type: 'Place' },
    { name: 'Beaconsfield, Victoria', type: 'Place' },
    { name: 'Officer, Victoria', type: 'Place' },
    { name: 'Pakenham, Victoria', type: 'Place' },
    { name: 'Drouin, Victoria', type: 'Place' },
    { name: 'Warragul, Victoria', type: 'Place' },
    { name: 'Moe, Victoria', type: 'Place' },
    { name: 'Traralgon, Victoria', type: 'Place' },
    { name: 'Sale, Victoria', type: 'Place' },
    { name: 'Bairnsdale, Victoria', type: 'Place' },
    { name: 'Leongatha, Victoria', type: 'Place' },
    { name: 'Cardinia Shire', type: 'AdministrativeArea' },
    { name: 'Yarra Ranges Shire', type: 'AdministrativeArea' },
    { name: 'Baw Baw Shire', type: 'AdministrativeArea' },
    { name: 'Latrobe City', type: 'AdministrativeArea' },
    { name: 'Wellington Shire', type: 'AdministrativeArea' },
    { name: 'East Gippsland Shire', type: 'AdministrativeArea' },
    { name: 'South Gippsland Shire', type: 'AdministrativeArea' },
    { name: 'Casey, Victoria', type: 'AdministrativeArea' },
  ],
  capabilities: [
    'Crushed rock driveway restoration',
    'Excavation',
    'Earthworks',
    'Retaining walls',
    'Drainage',
    'Site cuts',
    'Tight access excavation',
  ],
  additionalOffer: {
    name: 'Excavation and Earthworks',
    description:
      'Site cuts, land clearing, cut and fill, dam work, drainage, tight access excavation.',
  },
  drivewayClip: {
    beforeSrc: '/videos/driveway-before.mp4',
    afterSrc: '/videos/driveway-after.mp4',
    beforeAlt: 'Worn dirt and leaf-litter yard before crushed rock restoration',
    afterAlt: 'Same yard finished with fresh compacted crushed rock pad and basalt feature stones',
  },
  siteLabels: {
    stickyCtaLabel: 'Get a free quote',
    googleReviewsLabel: 'See all Google reviews',
    trustStripAbnLabel: 'ABN',
    trustStripInsuredLabel: 'insured',
    beforeLabel: 'Before',
    afterLabel: "ROCK'D",
  },
  otherPages: {
    review: {
      metaTitle: 'Leave a review',
      heading: 'How did we go?',
      intro:
        'Tap the stars. It takes a minute, and it helps the next local who is looking for a crew.',
      askHeading: 'Please leave us a review!',
      supportIntro:
        "Give us a chance to help! Shoot us a message and we'll give you a bell and sort it.",
      firstNameLabel: 'First name',
      lastNameLabel: 'Last name',
      phoneLabel: 'Phone',
      emailLabel: 'Email',
      contactHint: 'Either one is enough.',
      messageLabel: 'What went wrong?',
      messagePlaceholder: 'Give us hell',
      submitLabel: 'Send Feedback',
      sendingLabel: 'Sending...',
      googleLinkLabel: 'See all Google reviews',
    },
    testimonial: {
      metaTitle: 'Share a testimonial',
      metaDescription: "Share a testimonial with the Rock'n Earthworks team.",
      eyebrow: 'Testimonials',
      heading: 'Tell us how the job went',
      intro: "Did we build your driveway, wall or site? Tell us how it's held up.",
    },
  },
}
